import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { App, Breadcrumb, Button, Card, Checkbox, Modal, Space, Tabs, Tag, Typography } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { lessonsApi } from '../api/lessons';
import { Lesson, PracticeTask } from '../api/types';
import TaskListEditor from '../components/practice/TaskListEditor';
import MigrationPanel from '../components/practice/MigrationPanel';
import { buildMigrationPlan, StagedTask } from '../components/practice/migration';
import { withServerDetail } from '../api/errorDetail';

const { Title, Text } = Typography;

/**
 * A task is well-formed only with a non-empty title AND text. Both clients drop anything
 * else when parsing a practice body, and so does the server when counting task coverage —
 * a task that fails this can never be rendered, never be ticked, and therefore holds the
 * lesson permanently `Active`. Total by construction: a stored task may be missing the
 * field entirely, and a migrated one gets `''` from `card.title ?? ''`.
 */
const describeGaps = (task: Partial<PracticeTask> | undefined): string | null => {
  const noTitle = typeof task?.title !== 'string' || !task.title.trim();
  const noText = typeof task?.text !== 'string' || !task.text.trim();
  if (noTitle && noText) return 'no title and no text';
  if (noTitle) return 'no title';
  if (noText) return 'no text';
  return null;
};

const PracticeLessonPage: React.FC = () => {
  const { courseId, moduleId, lessonId } = useParams<{
    courseId: string; moduleId: string; lessonId: string;
  }>();
  const { apiClient } = useAuth();
  const { message } = App.useApp();

  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tasks, setTasks] = useState<PracticeTask[]>([]);
  const [staged, setStaged] = useState<StagedTask[]>([]);

  const lApi = useCallback(() => lessonsApi(apiClient!), [apiClient]);
  const practiceLesson = useMemo(
    () => lessons.find((l) => l.id === Number(lessonId)) ?? null,
    [lessons, lessonId],
  );

  /**
   * `keepStaged` guards the staging area. "Save tasks" is a header button, visible from the
   * Migrate tab, and it only writes the Tasks tab through `lessons/update` — the staged
   * entries are untouched by that call and are expensive to rebuild by hand, so silently
   * dropping them behind a success toast would be pure data loss. Reloading after Apply
   * still clears them, because Apply is what actually commits them.
   */
  const fetchData = useCallback(async (keepStaged = false) => {
    setLoading(true);
    try {
      const res = await lApi().getList(Number(moduleId));
      setLessons(res.data.items);
      const practice = res.data.items.find((l) => l.id === Number(lessonId));
      setTasks(Array.isArray(practice?.body?.tasks) ? practice!.body.tasks : []);
      if (!keepStaged) setStaged([]);
    } catch {
      message.error('Failed to load lessons');
    } finally {
      setLoading(false);
    }
  }, [lApi, moduleId, lessonId, message]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const invalidCount = tasks.filter((t) => describeGaps(t) !== null).length;
  const invalid = invalidCount > 0;

  // Apply writes the Tasks tab AND the staging area in one transaction, so both have to be
  // well-formed. Nothing upstream guarantees a migrated card had a title or a challengeText.
  const invalidStaged = staged
    .map((entry, index) => ({ index, title: entry.task?.title ?? '', gap: describeGaps(entry.task) }))
    .filter((entry): entry is { index: number; title: string; gap: string } => entry.gap !== null);
  const applyBlocked = invalid || invalidStaged.length > 0;

  const saveTasks = async () => {
    if (invalid) {
      message.error('Every task needs a title and text.');
      return;
    }
    const commit = async () => {
      setSaving(true);
      try {
        await lApi().update({ ...practiceLesson!, body: { tasks } });
        message.success('Tasks saved');
        fetchData(true);
      } catch (err) {
        message.error(withServerDetail('Failed to save tasks', err));
      } finally {
        setSaving(false);
      }
    };

    if (tasks.length === 0) {
      Modal.confirm({
        title: 'Save with no tasks?',
        content: 'A practice lesson with no tasks can never be completed, so it will hold the module counter below 100%. Consider setting the lesson to Disabled until you add tasks.',
        okText: 'Save anyway',
        onOk: commit,
      });
      return;
    }
    await commit();
  };

  const applyMigration = () => {
    if (applyBlocked) {
      message.error('Every task, staged or saved, needs a title and text.');
      return;
    }
    const sources = lessons.filter((l) => l.id !== Number(lessonId));
    const allStaged: StagedTask[] = [
      ...tasks.map((task) => ({ task, origin: null, keepInSource: false })),
      ...staged,
    ];
    const plan = buildMigrationPlan(Number(lessonId), allStaged, sources);
    const touched = plan.filter((p) => p.id !== Number(lessonId));

    Modal.confirm({
      title: 'Apply migration',
      width: 520,
      content: (
        <Space direction="vertical" size={4} style={{ marginTop: 12 }}>
          <Text>Practice lesson <Text strong>+{staged.length}</Text> task(s)</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            This also saves the current contents of the Tasks tab, including any unsaved edits.
          </Text>
          {touched.map((entry) => {
            const lesson = lessons.find((l) => l.id === entry.id)!;
            const before = Array.isArray(lesson.body?.cards) ? lesson.body.cards.length : 0;
            const after = (entry.body as { cards: unknown[] }).cards.length;
            return (
              <Text key={entry.id} type="secondary">
                {lesson.title} — {before - after} challenge card(s) removed
              </Text>
            );
          })}
          {touched.length === 0 && <Text type="secondary">No source lessons will be modified.</Text>}
        </Space>
      ),
      okText: 'Apply',
      onOk: async () => {
        try {
          await lApi().batchUpdateBodies(plan);
          message.success('Migration applied');
          fetchData();
        } catch (err) {
          message.error(withServerDetail('Migration failed — nothing was changed', err));
        }
      },
    });
  };

  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      <Breadcrumb
        items={[
          { title: <Link to="/courses">Courses</Link> },
          { title: <Link to={`/courses/${courseId}/modules`}>Modules</Link> },
          { title: <Link to={`/courses/${courseId}/modules/${moduleId}/lessons`}>Lessons</Link> },
          { title: practiceLesson?.title ?? `Lesson ${lessonId}` },
        ]}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3} style={{ margin: 0 }}>
          {practiceLesson?.title ?? 'Practice'} <Tag color="purple">Practice</Tag>
        </Title>
        <Button type="primary" icon={<SaveOutlined />} loading={saving}
          disabled={loading || !practiceLesson} onClick={saveTasks}>
          Save tasks
        </Button>
      </div>

      <Tabs
        items={[
          {
            key: 'tasks',
            label: `Tasks (${tasks.length})`,
            children: <TaskListEditor tasks={tasks} onChange={setTasks} />,
          },
          {
            key: 'migrate',
            label: `Migrate${staged.length ? ` (${staged.length} staged)` : ''}`,
            children: (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
                <MigrationPanel
                  lessons={lessons.filter((l) => l.id !== Number(lessonId))}
                  staged={staged}
                  onStage={(added) => setStaged((prev) => [...prev, ...added])}
                />
                <Card
                  title={`Staged for «${practiceLesson?.title ?? ''}» (${staged.length})`}
                  extra={
                    <Button type="primary" disabled={staged.length === 0 || applyBlocked} onClick={applyMigration}>
                      Apply changes
                    </Button>
                  }
                >
                  {invalid && (
                    <Text type="danger" style={{ display: 'block', marginBottom: 12 }}>
                      {invalidCount} task{invalidCount === 1 ? '' : 's'} on the Tasks tab {invalidCount === 1 ? 'is' : 'are'} missing a title or text.
                      Fix {invalidCount === 1 ? 'it' : 'them'} on the Tasks tab before applying.
                    </Text>
                  )}
                  {invalidStaged.length > 0 && (
                    <Text type="danger" style={{ display: 'block', marginBottom: 12 }}>
                      {invalidStaged.length} staged {invalidStaged.length === 1 ? 'entry has' : 'entries have'} nothing to render:{' '}
                      {invalidStaged.map((e) => `#${e.index + 1} «${e.title || '(no title)'}» — ${e.gap}`).join('; ')}.
                      Both apps drop a task without a title and text, so it would vanish from the
                      practice lesson while Apply still removes it from its source lesson, and the
                      lesson could never reach 100%. Remove {invalidStaged.length === 1 ? 'it' : 'them'} below,
                      or fix the source card first.
                    </Text>
                  )}
                  {staged.length === 0 ? (
                    <Text type="secondary">
                      Select tasks on the left and press Move. Nothing is written until you press Apply.
                    </Text>
                  ) : (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      {staged.map((entry, index) => {
                        const gap = describeGaps(entry.task);
                        return (
                        <Card key={entry.task.id} size="small">
                          <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Text strong type={gap ? 'danger' : undefined}>
                              #{String(index + 1).padStart(2, '0')} {entry.task.title || '(no title)'}
                            </Text>
                            {gap && (
                              <Text type="danger" style={{ fontSize: 12 }}>
                                Cannot be applied — {gap}.
                              </Text>
                            )}
                            <Text type="secondary" style={{ fontSize: 12 }}>{entry.task.text}</Text>
                            <Space>
                              <Checkbox
                                checked={entry.keepInSource}
                                onChange={(e) =>
                                  setStaged((prev) => prev.map((s, i) =>
                                    i === index ? { ...s, keepInSource: e.target.checked } : s))
                                }
                              >
                                <Text style={{ fontSize: 12 }}>Keep in source lesson</Text>
                              </Checkbox>
                              <Button size="small" danger
                                onClick={() => setStaged((prev) => prev.filter((_, i) => i !== index))}>
                                Remove
                              </Button>
                            </Space>
                            {entry.origin && (
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                from «{entry.origin.lessonTitle}»
                              </Text>
                            )}
                          </Space>
                        </Card>
                        );
                      })}
                    </Space>
                  )}
                </Card>
              </div>
            ),
          },
        ]}
      />
    </Space>
  );
};

export default PracticeLessonPage;
